import { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "../../../assets/css/AGGrid-custom.css";
import { numberFormatter } from "../../../utils/numberFormatter";
import { DEAL_ANALYSIS_PANEL_HEIGHT } from "./DealStatusBreakdownChart";

ModuleRegistry.registerModules([AllCommunityModule]);

type DealLike = any;

const DealNameRenderer = (params: ICellRendererParams<DealLike>) => {
  const deal = params.data;
  const name = params.value as string | undefined;
  const id = deal?.id;

  if (!id || typeof id !== "number") return name || "-";

  return (
    <a
      href={`/deals/${id}`}
      style={{ color: "#1B7A63", textDecoration: "underline" }}
    >
      {name || `ID ${id}`}
    </a>
  );
};

const getTotalLoanAmount = (deal: DealLike): number | null => {
  const ff = deal?.loan_amount_fully_funded;
  if (typeof ff === "number") return ff;
  const d1 = deal?.loan_amount_day_one;
  if (typeof d1 === "number") return d1;
  return null;
};

export function DealAnalysisDealsTable(props: {
  deals: DealLike[];
  title?: string;
  minHeight?: number;
}) {
  const { deals, title, minHeight = DEAL_ANALYSIS_PANEL_HEIGHT } = props;

  const rowData = useMemo(() => deals ?? [], [deals]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "deal_name",
        headerName: "Deal Name",
        width: 220,
        cellRenderer: DealNameRenderer,
      },
      {
        headerName: "Status",
        width: 140,
        valueGetter: (p) => p.data?.status?.status ?? "",
      },
      {
        headerName: "Kill Reason",
        width: 170,
        valueGetter: (p) => p.data?.kill_reason?.kill_reason ?? "",
      },
      {
        headerName: "Country",
        width: 170,
        valueGetter: (p) => {
          const countries = p.data?.countries;
          if (!Array.isArray(countries) || countries.length === 0) return "";
          return countries
            .map((c: any) => c?.nicename)
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        headerName: "Total Loan Amount",
        width: 160,
        valueGetter: (p) => getTotalLoanAmount(p.data),
        valueFormatter: numberFormatter,
        cellStyle: { textAlign: "right" as const },
      },
    ],
    [],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        background:
          "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography className="text-general-section-title">
          {title ?? "Deals"}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 1.5, md: 2 },
          py: 1.5,
        }}
      >
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, filter: true, sortable: true }}
            rowHeight={30}
            headerHeight={30}
            suppressCellFocus={true}
            suppressRowClickSelection={true}
            getRowId={({ data }) => String(data.id)}
          />
        </div>
      </Box>
    </Paper>
  );
}
